(() => {
  const initializedRoots = new WeakSet();

  /** @param {number} value @param {number} min @param {number} max */
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  // Manual layer alignment controls.
  // X/Y values are pixels. Positive X moves right; positive Y moves down.
  // Rotation values are degrees. Positive rotation turns clockwise.
  /** @type {Record<string, number>} */
  const layerX = { top: -7, middle: 0, bottom: 0 };
  /** @type {Record<string, number>} */
  const layerY = { top: -11, middle: 0, bottom: 22 };
  /** @type {Record<string, number>} */
  const layerRotation = { top: -0.5, middle: 0.5, bottom: 0 };
  /** @type {Record<string, number>} */
  const layerScaleStart = { top: 1.01, middle: 0.985, bottom: 0.92 };
  /** @type {Record<string, number>} */
  const layerScaleEnd = { top: 1.015, middle: 0.98, bottom: 0.96 };
  /** @type {Record<string, number>} */
  const mobileLayerX = { top: -7, middle: 0, bottom: 0 };
  /** @type {Record<string, number>} */
  const mobileLayerY = { top: -6, middle: 0, bottom: 12 };
  /** @type {Record<string, number>} */
  const mobileLayerRotation = { top: -0.5, middle: 0.5, bottom: 0 };
  /** @type {Record<string, number>} */
  const mobileLayerScale = { top: 1.01, middle: 0.985, bottom: 0.92 };
  /** @type {Record<string, number>} */
  const mobileOffsets = { top: -105, middle: 0, bottom: 85 };
  /** @type {Record<string, number>} */
  const mobileLayerScaleEnd = { top: 1.015, middle: 0.98, bottom: 0.96 };

  /** @param {HTMLElement} root */
  const updateRoot = (root) => {
    /** @type {NodeListOf<HTMLElement>} */
    const layers = root.querySelectorAll('[data-sole-layer]');
    /** @type {NodeListOf<HTMLElement>} */
    const callouts = root.querySelectorAll('[data-sole-callout]');
    /** @type {HTMLElement|null} */
    const track = root.querySelector('[data-sole-track]');
    /** @type {HTMLElement|null} */
    const stage = root.querySelector('[data-sole-stage]');
    /** @type {HTMLElement|null} */
    const calloutsContainer = root.querySelector('[class$="__callouts"]');
    if (!track || !stage) return;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const scrollY = window.scrollY || window.pageYOffset;
    const stageBounds = stage.getBoundingClientRect();
    const trackBounds = track.getBoundingClientRect();
    const stageDocumentTop = stageBounds.top + scrollY;
    const trackDocumentTop = trackBounds.top + scrollY;
    const sectionStart = trackDocumentTop;
    const endScroll = trackDocumentTop + track.offsetHeight - window.innerHeight;
    const rawStartScroll = stageDocumentTop + stage.offsetHeight - window.innerHeight;
    const earliestStartScroll = sectionStart;
    const latestStartScroll = Math.max(endScroll - window.innerHeight * 0.35, earliestStartScroll);
    const startScroll = clamp(rawStartScroll, earliestStartScroll, latestStartScroll);
    const available = Math.max(endScroll - startScroll, 1);
    const mobileProgress = clamp(
      (window.innerHeight - stageBounds.bottom) /
        Math.max(stage.offsetHeight * 0.8, 1),
      0,
      1,
    );
    const progress = isMobile ? mobileProgress : clamp((scrollY - startScroll) / available, 0, 1);
    const eased = progress * progress * (3 - 2 * progress);
    /** @type {Record<string, number>} */
    const offsets = { top: -180, middle: 0, bottom: 150 };

    layers.forEach((layer) => {
      const name = layer.dataset.soleLayer ?? '';
      const baseY = isMobile ? (mobileLayerY[name] ?? 0) : (layerY[name] ?? 0);
      const startScale = isMobile ? (mobileLayerScale[name] ?? 1) : (layerScaleStart[name] ?? 1);
      const endScale = isMobile
        ? (mobileLayerScaleEnd[name] ?? startScale)
        : (layerScaleEnd[name] ?? startScale);
      const x = isMobile ? (mobileLayerX[name] ?? 0) : (layerX[name] ?? 0);
      const y = baseY + (isMobile ? (mobileOffsets[name] ?? 0) : (offsets[name] ?? 0)) * eased;
      const rotation = isMobile ? (mobileLayerRotation[name] ?? 0) : (layerRotation[name] ?? 0);
      const scale = startScale + (endScale - startScale) * eased;
      layer.style.setProperty(
        'transform',
        `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${rotation}deg) scale(${scale})`,
        'important',
      );
    });

    // Keep each connector vertically centred on its moving sole layer.
    // Horizontal positioning remains controlled entirely by CSS.
    if (!isMobile && calloutsContainer) {
      const calloutsBounds = calloutsContainer.getBoundingClientRect();
      layers.forEach((layer) => {
        const name = layer.dataset.soleLayer ?? '';
        const callout = root.querySelector(`[data-sole-callout="${name}"]`);
        if (!callout) return;

        const layerBounds = layer.getBoundingClientRect();
        const layerCenter = layerBounds.top + layerBounds.height / 2;
        callout.style.top = `${layerCenter - calloutsBounds.top}px`;
      });
    }

    /** @type {Record<string, number>} */
    const thresholds = { top: 0.1, middle: 0.3, bottom: 0.5 };
    callouts.forEach((callout) => {
      if (isMobile) {
        // On mobile there is no sticky scroll — show all callouts immediately
        callout.style.removeProperty('top');
        callout.style.setProperty('--callout-opacity', '1');
        return;
      }
      const start = thresholds[callout.dataset.soleCallout ?? ''] ?? 0;
      const visibility = clamp((progress - start) / 0.16, 0, 1);
      callout.style.setProperty('--callout-opacity', visibility.toFixed(3));
    });
  };

  /** @param {HTMLElement} root */
  const initRoot = (root) => {
    if (initializedRoots.has(root)) return;
    initializedRoots.add(root);
    let ticking = false;

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        updateRoot(root);
      });
    };

    document.addEventListener('scroll', requestUpdate, { capture: true, passive: true });
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    window.addEventListener('load', requestUpdate, { once: true });
    window.addEventListener('pageshow', requestUpdate, { once: true });
    requestUpdate();
    window.setTimeout(requestUpdate, 250);

    const animationLoop = () => {
      updateRoot(root);
      window.requestAnimationFrame(animationLoop);
    };
    animationLoop();
  };

  const initAll = () => {
    /** @type {NodeListOf<HTMLElement>} */
    const roots = document.querySelectorAll('[data-sole-scroll]');
    roots.forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
